import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Avatar, Button, CircularProgress } from '@mui/material';
import { Comment, Schedule, Check } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { useSocketEvent } from '../hooks/useSocketEvent';
import MarkdownDisplay from '../components/MarkdownDisplay';
import { requestGetUnansweredClientComments } from '../redux/sagas/requests/project';
import { getAvatarUrl } from '../utils/avatarUtils';
import { formatTimeAgo } from '../utils/dateUtils';
import { addComment } from '../redux/ducks/comment';
import { showAlert } from '../redux/slices/alerts';
import { extractPlainText, runContentFilter } from '../utils/contentFilter';

const CANNED_REPLIES = [
    'Thank you!',
    'No problem.',
    'Looking into it.',
    'Will follow up shortly.',
];

const CannedReplies = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const CannedReplyButton = styled(Button)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    padding: theme.spacing(0.25, 1),
    minWidth: 0,
    textTransform: 'none',
    lineHeight: 1.3,
}));

const SendingSpinner = styled(CircularProgress)(({ theme }) => ({
    marginLeft: theme.spacing(0.5),
    alignSelf: 'center',
}));

const SentLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.typography.size.xs,
    color: theme.palette.success.main,
    marginTop: theme.spacing(1),
}));

const ProjectList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ProjectCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:last-child': {
        marginBottom: 0,
    },
}));

const ProjectHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
});

const ProjectName = styled(RouterLink)(({ theme }) => ({
    display: 'block',
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
}));

const CommentSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
}));

const CommentHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
});

const CommentAuthor = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const CommentDate = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
}));

const CommentText = styled(MarkdownDisplay)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Comment)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const CommentAvatar = styled(Avatar)(({ theme }) => ({
    width: 20,
    height: 20,
    fontSize: theme.typography.size.xxs,
}));

const CommentDateIcon = styled(Schedule)(({ theme }) => ({
    fontSize: theme.typography.size.xxs,
    marginRight: theme.spacing(0.5),
    verticalAlign: 'middle',
}));

const UnansweredClientCommentsWidget = ({ widgetId, onRemove }) => {
    const dispatch = useDispatch();
    const contentFilter = useSelector((state) => state.config.contentFilter);
    const { data: projects, isLoading, error, mutate } = useFetchQueue(widgetId, {
        apiCall: () => requestGetUnansweredClientComments(),
        cacheKey: 'unanswered-client-comments',
    });

    useSocketEvent('clientComment:new', (project) => mutate((prev) => {
        const list = (prev || []).filter((p) => p._id !== project._id);
        return [project, ...list];
    }));

    useSocketEvent('clientComment:resolved', ({ projectId }) => mutate((prev) => (prev || []).filter((p) => p._id !== projectId)));

    // per-project reply state: 'sending' | 'sent'
    const [replyState, setReplyState] = useState({});

    const visibleProjects = (projects || []).filter(p => replyState[p._id] !== 'sent');
    const hasData = visibleProjects.length > 0;
    const count = visibleProjects.length;

    const sendReply = (project, content) => {
        const comment = project.mostRecentComment;
        if (!comment) return;
        const check = runContentFilter(extractPlainText(content), contentFilter);
        if (check.blocked) {
            dispatch(showAlert({ severity: 'warning', message: check.reason }));
            return;
        }
        const thread = comment.thread || comment._id;
        setReplyState(s => ({ ...s, [project._id]: 'sending' }));
        dispatch(addComment('project', project._id, {
            content,
            privacy: 'Public',
            attachments: [],
            thread,
        }, (response) => {
            if (!response) {
                setReplyState(s => {
                    const next = { ...s };
                    delete next[project._id];
                    return next;
                });
                return;
            }
            setReplyState(s => ({ ...s, [project._id]: 'sent' }));
        }));
    };

    return (
        <BaseWidget
            title={`Unanswered Client Comments${!isLoading && count > 0 ? ` (${count})` : ''}`}
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No unanswered client comments
                    </Typography>
                </EmptyState>
            ) : (
                <ProjectList>
                    {visibleProjects.map((project) => {
                        const comment = project.mostRecentComment;
                        const author = comment?.user;
                        const authorName = author
                            ? `${author.first_name || ''} ${author.last_name || ''}`.trim() || author.email
                            : 'Unknown';
                        const state = replyState[project._id];

                        return (
                            <ProjectCard key={project._id}>
                                <ProjectHeader>
                                    <ProjectName to={`/account/${project.account?.slug}/project/${project._id}`}>
                                        {project.name}
                                    </ProjectName>
                                </ProjectHeader>

                                {comment && (
                                    <CommentSection>
                                        <CommentHeader>
                                            <CommentAvatar
                                                src={getAvatarUrl(author)}
                                            >
                                                {authorName.charAt(0).toUpperCase()}
                                            </CommentAvatar>
                                            <CommentAuthor>{authorName}</CommentAuthor>
                                            <CommentDate>
                                                <CommentDateIcon fontSize="inherit" />
                                                {formatTimeAgo(comment.createdAt || comment.created_at)}
                                            </CommentDate>
                                        </CommentHeader>
                                        <CommentText content={comment.content} />
                                        {state === 'sent' ? (
                                            <SentLabel>
                                                <Check fontSize="inherit" /> Reply sent
                                            </SentLabel>
                                        ) : (
                                            <CannedReplies className="widget-no-drag">
                                                {CANNED_REPLIES.map((text) => (
                                                    <CannedReplyButton
                                                        key={text}
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={state === 'sending'}
                                                        onClick={() => sendReply(project, text)}
                                                    >
                                                        {text}
                                                    </CannedReplyButton>
                                                ))}
                                                {state === 'sending' && (
                                                    <SendingSpinner size={12} />
                                                )}
                                            </CannedReplies>
                                        )}
                                    </CommentSection>
                                )}
                            </ProjectCard>
                        );
                    })}
                </ProjectList>
            )}
        </BaseWidget>
    );
};

export default UnansweredClientCommentsWidget;
